// External Libraries
import { useEffect, useRef, useState } from "react";
import { Search as SearchIcon } from "@carbon/icons-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSetRecoilState } from "recoil";

// Components
import { Body, BodyColor, BodyType, Heading, Search, TypographyColor, TypographyType } from "../../ui-elements";
import { IconButton } from "../Shadcn";
import { TableSkeletonLoader } from "../home/TableSkeleton";
import { GeoDataTable } from ".";

// Utilities
import { useCompareService } from "../../services";
import { geoState } from "../../states";
import { useDebounce, useHandleError } from "../../helpers";
import { RouteConstants } from "../../constants";

const CHIPS = [
    { type: "States", level: 2 },
    { type: "Districts", level: 3 },
    { type: "Sub-districts", level: 4 },
    { type: "Pincodes", level: 5 },
    { type: "Villages", level: 6 },
] as const;

export const GlobalSearchNew = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const compareService = useCompareService();
    const { getErrorMsg } = useHandleError();
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 500);
    const [allSearchResults, setAllSearchResults] = useState<Record<string, any>>({});
    const [chipCounts, setChipCounts] = useState<Record<string, number | null>>({});
    const [chipLoading, setChipLoading] = useState<Record<string, boolean>>({});
    const inputRef = useRef<HTMLInputElement>(null);
    const [selectedChip, setSelectedChip] = useState<string>('Districts');
    const setselectedGeo = useSetRecoilState(geoState);
    const [searchParams] = useSearchParams();
    const catchment = searchParams.get('catchment');

    const searchResults = allSearchResults[selectedChip] ?? null;


    const handleModal = () => {
        setShowModal(prev => !prev);
        setSearchTerm('');
        setSelectedChip('Districts');
        setAllSearchResults({});
        setChipCounts({});
        setChipLoading({});
    };

    const handleInputChange = (value: string) => setSearchTerm(value);

    const fetchAllChipResults = (searchKey: string) => {
        const initialLoading = Object.fromEntries(CHIPS.map(c => [c.type, true]));
        setChipLoading(initialLoading);
        setChipCounts({});
        setAllSearchResults({});

        CHIPS.forEach(chip => {
            const payload: any = {
                geoId: 1,
                mbp: false,
                geoLevel: chip.level,
                childLevel: chip.level,
                searchKey,
                pageNumber: 1,
                dataPerPage: 1000000
            };
            compareService.getAllVillages(payload)
                .then((response) => {
                    setAllSearchResults(prev => ({ ...prev, [chip.type]: response }));
                    setChipCounts(prev => ({ ...prev, [chip.type]: response?.data?.length ?? 0 }));
                })
                .catch((error) => {
                    getErrorMsg(error);
                    setChipCounts(prev => ({ ...prev, [chip.type]: null }));
                })
                .finally(() => {
                    setChipLoading(prev => ({ ...prev, [chip.type]: false }));
                });
        });
    }

    type HierarchyObject = {
        country?: number;
        state?: number;
        district?: number;
        sub_district?: number;
        village?: number;
    };

    function hierarchyToObject(hierarchy: number[]): HierarchyObject {
        const keys = ['country', 'state', 'district', 'sub_district', 'village'] as const;
        const obj: HierarchyObject = {};
        for (let i = 0; i < hierarchy.length; i++) {
            obj[keys[i]] = hierarchy[i];
        }
        return obj;
    }

    type HierarchyKey = 'country' | 'state' | 'district' | 'sub_district' | 'village';

    const handleListPress = (item: any) => {
        const selected = hierarchyToObject(item.hierarchy);

        setselectedGeo(selected);
        handleModal();
        if ((pathname !== RouteConstants.dashboard && pathname !== RouteConstants.root) || catchment) {
            const params = new URLSearchParams();
            const selGeo: Record<string, any> = {};

            const hierarchyKeys: HierarchyKey[] = ['country', 'state', 'district', 'sub_district', 'village'];

            hierarchyKeys.forEach(key => {
                if (selected[key] !== undefined && selected[key] !== null) {
                    params.append(key, selected[key]!.toString());
                    selGeo[key] = selected[key];
                }
            });

            setTimeout(() =>
                navigate({
                    pathname: pathname === RouteConstants.dashboard ? RouteConstants.dashboard : RouteConstants.root,
                    search: `?${params.toString()}`,
                }), 0);
        }
    }

    useEffect(() => {
        if (inputRef.current && showModal) {
            inputRef.current.focus();
        }
    }, [showModal]);

    useEffect(() => {
        if (debouncedSearch.length > 2) {
            fetchAllChipResults(debouncedSearch);
        } else {
            setAllSearchResults({});
            setChipCounts({});
            setChipLoading({});
        }
    }, [debouncedSearch]);

    return (
        <div
            className="help-button-wrapper d-flex flex-column align-items-center justify-content-center position-relative h-100"
            tabIndex={0}
        >
            <IconButton
                variant="outline"
                size="small"
                rounded="full"
                onClick={handleModal}
                className="border-epic-neutral-10"
            >
                <SearchIcon size={12} />
            </IconButton>
            <Body
                type={BodyType.p3}
                color={BodyColor.primary}
                classname="padding-1 help-text"
            >
                Search Region
            </Body>
            {showModal &&
                <div className="p-0 m-0">
                    <div className="backdrop-overlay position-fixed top-0 start-0 w-100 h-100 bg-dark opacity-50 combodropdown-backdrop" onClick={handleModal} />
                    <div className="global-search-modal d-flex flex-column justify-content-center align-items-center padding-5 rounded h-auto">
                        <Search
                            handleInputChange={handleInputChange}
                            searchTerm={searchTerm}
                            hideSuggestionBox={true}
                            placeholderValue="Search"
                            classname="height-48px w-100 border"
                            ref={inputRef}
                        />
                        {searchTerm.length > 0
                            && <div className="w-100 d-flex flex-row align-items-center gap-1 my-3">
                                {CHIPS.map((chip, idx) => (
                                    <Body key={idx} type={BodyType.p3} color={BodyColor.primary} classname={`badge rounded-pill d-flex align-items-center gap-1 ${selectedChip === chip.type ? 'fw-500 border border-dark bg-white' : 'fw-400 bg-primary-hover'}`} onClick={() => setSelectedChip(chip.type)}>
                                        {chip.type}
                                        {debouncedSearch.length > 2 && chipLoading[chip.type]
                                            ? <span className="spinner-border spinner-border-sm" style={{ width: '0.65rem', height: '0.65rem', borderWidth: '0.12em' }} />
                                            : chipCounts[chip.type] != null && chipCounts[chip.type]! > 0
                                                ? <Body key={idx} type={BodyType.p4} color={BodyColor.primary} classname="rounded-pill bg-black-19 ms-1" style={{ padding: '0.15em 0.4em' }}>{chipCounts[chip.type]}</Body>
                                                : null
                                        }
                                    </Body>
                                ))}
                            </div>
                        }
                        {
                            searchTerm.length > 2
                            && <div className="h-100 w-100 d-flex flex-column justify-content-start align-items-start global-searah-li-container no-scrollbar">
                                {chipLoading[selectedChip]
                                    ? <div className="d-flex justify-content-center align-items-center h-75">
                                        <TableSkeletonLoader rows={4} />
                                    </div>
                                    : (
                                        selectedChip === "Villages" && searchResults
                                            ? <div className="h-100 w-100 pt-2">
                                                {/* table will come here */}
                                                <GeoDataTable results={searchResults} handleListPress={handleListPress} />
                                            </div>
                                            // list
                                            : <div className="w-100 list-group list-group-flush pt-2">
                                                <Heading
                                                    title={`${selectedChip} (${searchResults?.data?.length ?? 0})`}
                                                    type={TypographyType.h5}
                                                    colour={TypographyColor.secondary}
                                                />
                                                {searchResults && Object.keys(searchResults)?.length > 0
                                                    ? searchResults?.data?.map((item: any, itemIndex: number) => {
                                                        return (
                                                            <div
                                                                key={item?.geoId}
                                                                className={`w-100 d-flex justify-content-between align-items-center mbp-tooltip-parent position-relative p-2 global-search-item ${(item?.hierarchy && item?.hierarchy?.length > 0) ? 'cursor-pointer' : ''}`}
                                                                onClick={() => handleListPress(item)}
                                                            >
                                                                <div className="d-flex align-items-center flex-grow-1">
                                                                    <div className={`d-flex align-items-center gap-1`}>
                                                                        <div className="">
                                                                            <Heading
                                                                                title={item?.geoName}
                                                                                type={TypographyType.h5}
                                                                                colour={TypographyColor.primary}
                                                                            />
                                                                        </div>
                                                                        {item.presence_types ? (
                                                                            item.presence_types?.map((presenceData: any, index: number) => (
                                                                                <div className='d-flex flex-row align-items-center justify-content-center position-relative bg-light-grey gap-2 padding-left-right-2 padding-top-bottom-1 rounded'
                                                                                >
                                                                                    <img
                                                                                        key={index}
                                                                                        src={presenceData?.icon}
                                                                                        alt=""
                                                                                        className=""
                                                                                        style={{ height: '1.5vh' }}
                                                                                    />
                                                                                    <Body
                                                                                        type={BodyType.p3}
                                                                                        color={BodyColor.primary}
                                                                                        classname="p-0 m-0"
                                                                                    >
                                                                                        {presenceData?.properties?.count}
                                                                                    </Body>
                                                                                    {/* {hoveredPresence?.row === itemIndex && hoveredPresence?.index === index && (
                                                                                        <div style={{ position: 'absolute', bottom: '140%', left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
                                                                                            <div
                                                                                                style={{
                                                                                                    position: 'relative',
                                                                                                    backgroundColor: 'white',
                                                                                                    padding: '10px 14px',
                                                                                                    borderRadius: '8px',
                                                                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                                                                    fontSize: '15px',
                                                                                                    textAlign: 'center',
                                                               
                                                                                                    whiteSpace: 'nowrap',
                                                                                                }}
                                                                                            >
                                                                                                <PresenceToolTip presenceData={presenceData} />
                                                                                                <div
                                                                                                    style={{
                                                                                                        content: '""',
                                                                                                        position: 'absolute',
                                                                                                        top: '100%',
                                                                                                        left: '50%',
                                                                                                        transform: 'translateX(-50%)',
                                                                                                        width: 0,
                                                                                                        height: 0,
                                                                                                        borderLeft: '6px solid transparent',
                                                                                                        borderRight: '6px solid transparent',
                                                                                                        borderTop: '6px solid white',
                                                                                                        borderBottomLeftRadius: '5px',
                                                                                                        borderBottomRightRadius: '5px'
                                                                                                    }}
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    )} */}
                                                                                </div>
                                                                            ))
                                                                        ) : null}
                                                                    </div>
                                                                </div>

                                                                {/* Show parent info if available */}
                                                                {item?.parents && Array.isArray(item?.parents) && item?.parents?.length > 0 && (
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        {item?.parents?.map((parent: any, idx: number) => (
                                                                            <Body key={idx} type={BodyType.p4} color={BodyColor.secondary} classname="badge rounded-pill bg-primary-hover">
                                                                                {parent?.geoName}
                                                                            </Body>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                    : <div className="w-100 d-flex justify-content-center align-items-start py-4 px-3">
                                                        <Body type={BodyType.p3} color={BodyColor.secondary}>
                                                            No match found!
                                                        </Body>
                                                    </div>
                                                }

                                                {searchResults && searchResults?.data?.length === 0 && (
                                                    <div className="text-center py-4">
                                                        <Body type={BodyType.p3} color={BodyColor.secondary} classname="">
                                                            No data found
                                                        </Body>
                                                    </div>
                                                )}
                                            </div>
                                    )
                                }
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    )
}